import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect } from "react-redux";
import {
  getColleges,
  getDepartmentAdmins,
  getDepartmentName,
} from "../../../../../action/common/commonActionCreator";
import { createDepartment } from "../../../../../action/masters/departmentActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ICreateDepartment,
  IDepartmentData,
} from "../../../../../models/master/department";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { maxImageSize } from "../../../../../constants/commonConstants";

const DepartmentCreate = (props: ICreateDepartment) => {
  const {
    createDepartment,
    setShowCreate,
    getColleges,
    colleges,
    getAllDepartments,
    getLoginUsers,
    loginUsers,
    getDepartmentName,
    departmentName,
  } = props;

  const initialData: IDepartmentData = {
    id: null,
    name: "",
    externalId: 0,
    file: null,
    description: "",
    isActive: true,
    collegeId: 0,
    address: "",
    location: "",
    email: "",
    phoneNumber: "",
    departmentLoginUserId: "",
    collegeDepartmentName: "",
  };

  // states

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    let _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "collegeId") {
      let _item =
        colleges &&
        colleges.find((college) => college.id === event.target.value);
      _data.collegeName = _item?.name;
      if (_data.collegeName) getDepartmentName(_data.collegeName);
    }
    if (event.target.name === "name") {
      let selectedDepartment = departmentName.find(
        (department) => department.id === event.target.value
      );
      setName(selectedDepartment?.name);
      _data.name = event.target.value;
      _data.collegeDepartmentName = `${_data.collegeName}-${selectedDepartment?.name}`;
    }

    errors && delete errors[event.target.name];
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const schema = Joi.object().keys({
    name: Joi.number().greater(0).required().label("Select Department"),
    collegeId: Joi.number().greater(0).required().label("Select College"),
    description: Joi.string().required().label("Description is required"),
    location: Joi.string().required().label("Location is required"),
    phoneNumber: Joi.string()
      .length(10)
      .required()
      .label("Phone number must be 10 digits"),
    address: Joi.string().required().label("Address is required"),
    email: Joi.string()
      .regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
      .required()
      .label("Enter valid email"),
    file: Joi.object().required().label("File is required"),
  });

  const onSave = async () => {
    const validationErrors = validateFields(data, schema);
    if (validationErrors === undefined) {
      if (data.departmentLoginUserId && loginUsers) {
        const selectedUser = loginUsers.find(
          (user: any) => user.id === data.departmentLoginUserId
        );
        if (selectedUser) {
          data.departmentLoginUserName = selectedUser.name;
        }
      }

      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("name", name);
      formData.append("externalId", data.name);
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("collegeDepartmentName", data.collegeDepartmentName);
      formData.append("address", data.address);
      formData.append("location", data.location);
      formData.append("phoneNumber", data.phoneNumber);
      formData.append("email", data.email);
      formData.append("collegeId", data.collegeId.toString() || "");
      formData.append(
        "departmentLoginUserId",
        data.departmentLoginUserId.toString() || ""
      );
      formData.append(
        "departmentLoginUserName",
        data.departmentLoginUserName || ""
      );
      formData.append("isActive", data.isActive.toString());
      setLoading(true);
      setIsDisabled(true);
      try {
        await createDepartment(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllDepartments();
      setShowCreate(false);
    }
  };

  useEffect(() => {
    getColleges();
    getLoginUsers();
  }, []);

  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Add New Department
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowCreate(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"University Management"}
              onChange={onChange}
              isMandatory={true}
              value={data.collegeId}
              name={"collegeId"}
              optionsList={sortAlphabetically(colleges)}
            />
            {errors?.collegeId && (
              <div className="text-danger">{errors.collegeId}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Department Name "}
              onChange={onChange}
              isMandatory={true}
              value={data.name}
              name={"name"}
              optionsList={sortAlphabetically(departmentName) || []}
            />
            {errors?.name && <div className="text-danger">{errors.name}</div>}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Address "}
              onChange={onChange}
              isMandatory={true}
              value={data.address}
              name={"address"}
              maxLength={200}
            />
            {errors?.address && (
              <div className="text-danger">{errors.address}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Location "}
              onChange={onChange}
              isMandatory={true}
              value={data.location}
              name={"location"}
              maxLength={50}
            />
            {errors?.location && (
              <div className="text-danger">{errors.location}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Phone Number "}
              onChange={onChange}
              isMandatory={true}
              value={data.phoneNumber}
              name={"phoneNumber"}
              maxLength={10}
            />
            {errors?.phoneNumber && (
              <div className="text-danger">{errors.phoneNumber}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Email "}
              onChange={onChange}
              isMandatory={true}
              value={data.email}
              name={"email"}
              maxLength={50}
            />
            {errors?.email && <div className="text-danger">{errors.email}</div>}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">
              Department Logo<i className="label-asterisk"> * </i>
            </label>
            <div className="inputfile-flex">
              {imagePreview && (
                <div className="inputfile-img">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
              )}
              <div className="inputfile-wrap">
                <input
                  type="file"
                  name="file"
                  id="image"
                  className="form-control d-none"
                  required
                  onChange={onFileChange}
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon, text/html"
                />
                <div className="inputfile">
                  <label htmlFor="image">Choose File</label>
                  <span>
                    {imageName
                      ? imageName.trim().split("/").pop()
                      : "No File Choosen"}
                  </span>
                </div>
              </div>
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Login user ID "}
              onChange={onChange}
              isMandatory={false}
              value={data.departmentLoginUserId}
              name={"departmentLoginUserId"}
              optionsList={sortAlphabetically(loginUsers)}
            />
            {errors?.departmentLoginUserId && (
              <div className="text-danger">{errors.departmentLoginUserId}</div>
            )}
          </div>
          <div className="col-md-12 form-group">
            <label className="profile_details_text">
              Description<i className="label-asterisk"> * </i>
            </label>
            <textarea
              name="description"
              value={data.description}
              onChange={onChange}
              maxLength={1000}
            ></textarea>
            {errors?.description && (
              <div className="text-danger">{errors.description}</div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={isDisabled}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowCreate(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createDepartment: (data: any, callBack: Function) =>
    createDepartment(data, callBack),
  getColleges: () => getColleges(),
  getLoginUsers: () => getDepartmentAdmins(),
  getDepartmentName: (college: string) => getDepartmentName(college),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    loginUsers: state.common.loginUsers,
    departmentName: state.common.departmentName,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentCreate);
